import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';
import { AlertCircle, Clock, RefreshCw } from 'lucide-react';
import {
  LEARNER_STUDY,
  LEARNER_CERTIFICATION_DETAIL,
  LEARNER_LEARNING_PATH_DETAIL,
  LEARNER_DASHBOARD,
} from '@/constants/constant-routes';

/** What to tell someone whose launch failed. */
const LTI_ERROR_MESSAGE: Record<string, string> = {
  lti_unknown_platform:
    'This LMS is not registered in Skillzen, or its registration has been suspended. ' +
    'Ask your Skillzen administrator to check it under Enterprise → LTI Integrations.',
  lti_deep_linking_disabled:
    'Content selection is turned off for this LMS in Skillzen. Ask your administrator to ' +
    'enable Deep Linking under Enterprise → LTI Integrations.',
  lti_deployment_suspended:
    'This connection between your LMS and Skillzen has been suspended. Ask your Skillzen ' +
    'administrator to re-activate it under Enterprise → LTI Integrations.',
  lti_not_enabled:
    'Skillzen is not available from your LMS right now. Ask your Skillzen administrator to ' +
    'turn on LTI Integration under Business Settings.',
  lti_user_inactive:
    'Your Skillzen account is not active. Please contact your Skillzen administrator.',
  lti_email_link_disabled:
    'Your email already belongs to a Skillzen account, and this LMS is not allowed to link to ' +
    'it. Ask your Skillzen administrator to allow linking by email for this LMS.',
  lti_ambiguous_platform:
    'Skillzen found more than one registration for this LMS. Ask your administrator to check ' +
    'the Client ID under Enterprise → LTI Integrations.',
  lti_invalid_target_link_uri:
    'This LMS link does not point to this Skillzen site. Ask your administrator to check the ' +
    'tool URLs configured in the LMS.',
  launch_failed:
    'Skillzen could not verify this launch. Please try again from your LMS, and if it keeps ' +
    'happening ask your administrator to check the LTI configuration.',
};

/** Welcome toast per launched content type; anything else stays generic. */
const LTI_WELCOME_MESSAGE: Record<string, string> = {
  COURSE: 'Welcome! Opening your course.',
  CERTIFICATION: 'Welcome! Opening your certification.',
  LEARNING_PATH: 'Welcome! Opening your learning path.',
};
const LTI_WELCOME_DEFAULT = 'Welcome! Opening Skillzen.';

/** Where an LTI launch should land. */
const resolveLtiTarget = (params: URLSearchParams): string | null => {
  const lti = params.get('lti');
  if (!lti || lti === 'error' || lti === 'unmapped') return null;

  const resourceId = params.get('resource_id');
  if (!resourceId) return LEARNER_DASHBOARD;

  switch (params.get('resource_type')) {
    case 'COURSE':
      return `${LEARNER_STUDY}/${resourceId}`;
    case 'CERTIFICATION':
      return `${LEARNER_CERTIFICATION_DETAIL}/${resourceId}`;
    case 'LEARNING_PATH':
      return `${LEARNER_LEARNING_PATH_DETAIL}/${resourceId}`;
    default:
      return LEARNER_DASHBOARD;
  }
};

export default function HandleToken() {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();
  const hasHandledToken = useRef(false);
  const [ltiError, setLtiError] = useState<string | null>(null);
  const [ltiNotice, setLtiNotice] = useState(false);

  useEffect(() => {
    if (hasHandledToken.current) return;

    const handleToken = async () => {
      hasHandledToken.current = true;
      const searchParams = new URLSearchParams(location.search);

      // A failed launch has no session, so there is nothing to validate and
      // nowhere to send them. Say what went wrong instead — falling through
      // The launch worked, but nobody has told Skillzen what this activity
      // should open. Saying so beats dropping them on a dashboard.
      if (searchParams.get('lti') === 'unmapped') {
        setLtiNotice(true);
        return;
      }

      if (searchParams.get('lti') === 'error') {
        const code = searchParams.get('error') || 'launch_failed';
        console.error('LTI launch failed:', code);
        setLtiError(LTI_ERROR_MESSAGE[code] ?? LTI_ERROR_MESSAGE.launch_failed);
        return;
      }

      // Check for SSO error
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (error) {
        console.error('SSO authentication failed:', errorDescription);
        toast.error(errorDescription || 'Authentication failed. Please try again.');
        navigate('/login', {
          replace: true,
          state: { error: errorDescription }
        });
        return;
      }

      // With cookie-based auth, the server sets HttpOnly cookies on SSO redirect.
      // Validate the session via /me (cookies are sent automatically).
      try {
        const isValid = await checkAuth();

        if (!isValid) {
          throw new Error('Failed to validate authentication');
        }

        // An LTI launch names the resource the learner clicked in their LMS, so
        // it wins over any stored SSO return URL — landing elsewhere would drop
        const ltiTarget = resolveLtiTarget(searchParams);
        if (ltiTarget) {
          localStorage.removeItem('sso_return_url');
          toast.success(LTI_WELCOME_MESSAGE[searchParams.get('resource_type') ?? ''] ?? LTI_WELCOME_DEFAULT);
          navigate(ltiTarget, { replace: true });
          return;
        }

        toast.success('Welcome! Successfully authenticated via SSO.');

        // Get return URL from state or default to dashboard
        const returnUrl = localStorage.getItem('sso_return_url') || '/dashboard';
        localStorage.removeItem('sso_return_url');
        navigate(returnUrl, { replace: true });
      } catch (error) {
        console.error('Failed to complete authentication:', error);
        toast.error('Authentication failed. Please try again.');
        navigate('/login', { replace: true });
      }
    };

    handleToken();
  }, []);

  if (ltiError) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 px-4">
        <div className="text-center max-w-md space-y-4">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
          <div>
            <p className="text-lg font-semibold text-gray-900">This link could not be opened</p>
            <p className="text-gray-500 mt-2">{ltiError}</p>
          </div>
        </div>
      </div>
    );
  }

  if (ltiNotice) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 px-4">
        <div className="text-center max-w-md space-y-4">
          <Clock className="mx-auto h-12 w-12 text-amber-500" />
          <div>
            <p className="text-lg font-semibold text-gray-900">This activity isn't ready yet</p>
            <p className="text-gray-500 mt-2">
              You're signed in, but your administrator still needs to choose what this link
              should open. They have been notified — please try again shortly, or mention it
              to your teacher.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-screen bg-gray-50">
      <div className="text-center space-y-4">
        <RefreshCw className="mx-auto h-12 w-12 text-primary-600 animate-spin" />
        <div>
          <p className="text-lg font-semibold text-gray-900">Completing Authentication</p>
          <p className="text-gray-500 mt-2">Please wait while we securely log you in...</p>
        </div>
      </div>
    </div>
  );
}
